import React, { useState } from "react";
import { useTheme } from "next-themes";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ArrowUpRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import SectionHeader from "@/components/brams/SectionHeader";
import Reveal from "@/components/brams/Reveal";

const projects = [
  {
    id: 1,
    title: "Flyer",
    category: "Coach sportif",
    year: "2025",
    image:
      "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/qseq49id_ChatGPT%20Image%2027%20juin%202026%2C%2011_34_59.png",
    brief:
      "LEFA ONE TRAINER souhaitait annoncer l'ouverture prochaine de sa salle de sport privée à travers un flyer impactant, mettant en avant ses prestations de coaching personnalisé, son suivi nutritionnel et son accompagnement sur mesure afin de susciter l'intérêt et générer des réservations avant l'ouverture.",
    solution:
      "Création d'un flyer premium au design noir et blanc avec une touche de rouge pour attirer l'attention sur les appels à l'action. Une photographie immersive du coach en pleine séance renforce l'authenticité, tandis qu'une hiérarchie typographique claire met en valeur les services proposés, les objectifs des futurs adhérents ainsi que les informations de contact.",
    outcome:
      "Un support de communication professionnel ayant permis de renforcer l'image de marque de LEFA ONE TRAINER, de créer de l'attente autour de l'ouverture de la salle et de générer de nombreuses demandes d'informations ainsi que des réservations de séances d'essai avant le lancement officiel.",
  },
  {
    id: 2,
    title: "Carte de visite — C.Clean Canalisation",
    category: "Curage · Débouchage · Passage caméra",
    year: "2025",
    image:
      "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/ryz2lfxj_ChatGPT%20Image%2027%20juin%202026%2C%2011_41_20.png",
    brief:
      "C.Clean Canalisation souhaitait disposer d'une carte de visite professionnelle et moderne afin de présenter son identité visuelle, ses services de curage, débouchage et passage caméra, tout en permettant aux particuliers et aux professionnels de le contacter rapidement.",
    solution:
      "Création d'une carte de visite recto-verso au design premium, utilisant les couleurs de la marque (bleu, orange et blanc) pour renforcer son identité. Le recto met en avant un logo percutant et mémorable, tandis que le verso présente de manière claire les prestations, les avantages de l'entreprise et les informations de contact avec une hiérarchie visuelle efficace.",
    outcome:
      "Une carte de visite impactante qui valorise l'image professionnelle de C.Clean Canalisation, facilite la prise de contact et renforce la crédibilité de l'entreprise auprès de ses clients particuliers et professionnels.",
  },
  {
    id: 3,
    title: "Logo — Marque de vêtements",
    category: "ANTIDOTE PARIS · Streetwear",
    year: "2025",
    image:
      "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/nsjn7lac_ChatGPT%20Image%2027%20juin%202026%2C%2012_31_42.png",
    brief:
      "ANTIDOTE PARIS souhaitait créer un logo moderne et distinctif pour lancer sa marque de vêtements streetwear premium. L'objectif était de concevoir une identité visuelle forte, reconnaissable au premier regard et capable de représenter les valeurs de la marque sur l'ensemble de ses collections et de ses supports de communication.",
    solution:
      "Conception d'un logo minimaliste en noir, blanc et rouge, intégrant une seringue comme élément central afin de symboliser l'univers unique de la marque. L'identité a ensuite été déclinée sur des vêtements oversize (t-shirts et sweats à capuche) grâce à des mockups photoréalistes mettant en valeur le design dans un environnement premium.",
    outcome:
      "Une identité visuelle forte et immédiatement reconnaissable, renforçant le positionnement premium d'ANTIDOTE PARIS. Le logo est désormais utilisé sur les collections textiles, les réseaux sociaux, le site internet et les supports de communication, garantissant une image de marque cohérente et mémorable.",
  },
  {
    id: 4,
    title: "Dépliant 2 volets — Restaurant",
    category: "Bowl B Sushi · Restauration",
    year: "2025",
    image:
      "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/0qfriran_ChatGPT%20Image%2027%20juin%202026%2C%2012_57_01.png",
    brief:
      "Bowl B Sushi souhaitait présenter l'ensemble de sa carte à travers un dépliant 2 volets moderne et attractif, permettant aux clients de découvrir facilement les différentes catégories de produits, les menus, les pokés, les sushis et les informations de commande.",
    solution:
      "Création d'un dépliant 2 volets recto-verso au design dynamique, mêlant des couleurs vives, des visuels gourmands et une hiérarchie claire des informations. Chaque rubrique a été organisée pour offrir une lecture fluide, mettant en avant les produits, les tarifs, les formules et les plateformes de livraison tout en respectant l'identité visuelle du restaurant.",
    outcome:
      "Un support de communication professionnel qui valorise l'offre de Bowl B Sushi, facilite la lecture du menu et améliore l'expérience client. Le dépliant est utilisé en restaurant, en distribution locale et lors des commandes à emporter, renforçant la visibilité de l'établissement et favorisant les ventes.",
  },
  {
    id: 5,
    title: "Menu Board — Fast-food",
    category: "Chicken Empire · Restauration rapide",
    year: "2025",
    image:
      "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/z1u0aqn5_ChatGPT%20Image%2027%20juin%202026%2C%2013_06_53.png",
    brief:
      "Chicken Empire souhaitait présenter l'ensemble de sa carte à travers une série de menu boards modernes et impactants, mettant en valeur ses spécialités, ses menus, ses burgers, ses wraps, ses accompagnements et ses offres afin de faciliter le choix des clients en restaurant.",
    solution:
      "Création d'une collection de menu boards au design street food premium, avec une identité graphique forte basée sur des couleurs vives, une typographie percutante et des visuels gourmands en haute qualité. Chaque panneau est organisé avec une hiérarchie claire permettant de mettre en avant les produits, les tarifs et les promotions tout en garantissant une lecture rapide et efficace.",
    outcome:
      "Des menu boards professionnels qui renforcent l'image de marque de Chicken Empire, améliorent l'expérience client au moment de la commande et valorisent l'ensemble de la carte. Cette nouvelle présentation offre une meilleure visibilité des produits et contribue à augmenter les ventes grâce à une communication visuelle claire et attractive.",
  },
  {
    id: 6,
    title: "Visuels Réseaux Sociaux — Parfumerie · Mode",
    category: "Maison CDS · Kadeer_Boli Shop",
    year: "2025",
    image:
      "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/bmxv0n67_ChatGPT%20Image%2027%20juin%202026%2C%2014_29_04.png",
    brief:
      "Deux enseignes spécialisées dans la mode et la parfumerie souhaitaient développer leur présence sur les réseaux sociaux grâce à des visuels premium capables d'attirer immédiatement l'attention, de mettre en valeur leurs produits et de renforcer leur identité de marque.",
    solution:
      "Création de visuels publicitaires modernes adaptés aux réseaux sociaux, avec une direction artistique haut de gamme, des compositions dynamiques, une hiérarchie typographique claire et des mises en scène mettant en valeur les produits de luxe. Chaque création respecte l'identité visuelle de la marque tout en optimisant la lisibilité et l'impact marketing.",
    outcome:
      "Des visuels professionnels qui renforcent l'image des deux enseignes, améliorent leur communication digitale et augmentent l'engagement sur les réseaux sociaux. Les créations permettent de présenter efficacement les produits, de valoriser les offres commerciales et de développer la visibilité des marques auprès de leur clientèle.",
  },
];

export default function Portfolio() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [active, setActive] = useState(null);

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className={cn("relative py-28 md:py-36", isDark ? "bg-black" : "bg-white")}
    >
      {/* Top gradient divider */}
      <div
        className="absolute top-0 inset-x-0 h-px"
        style={{
          background: isDark
            ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)"
            : "linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="Portfolio"
          title={
            <>
              Des créations qui valorisent votre{" "}
              <em className="not-italic opacity-60">image de marque</em>.
            </>
          }
          subtitle="Découvrez une sélection de réalisations conçues pour accompagner des professionnels de différents secteurs. Chaque projet est pensé pour allier créativité, efficacité et impact visuel, afin de répondre aux objectifs de chaque client."
        />

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {projects.map((p, i) => (
            <Reveal key={p.id} delay={i * 80}>
              <ProjectCard project={p} isDark={isDark} onOpen={() => setActive(p)} />
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="portfolio-dialog"
          className={cn(
            "max-w-3xl max-h-[90vh] overflow-y-auto p-0 rounded-2xl border",
            isDark ? "bg-black border-white/10 text-white" : "bg-white border-black/10 text-black"
          )}
        >
          {active && (
            <>
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={active.image}
                  alt={active.title}
                  className={cn(
                    "w-full h-full object-cover",
                    "grayscale"
                  )}
                  loading="lazy"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: isDark
                      ? "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.9))"
                      : "linear-gradient(180deg, transparent 40%, rgba(255,255,255,0.9))",
                  }}
                />
                <button
                  onClick={() => setActive(null)}
                  data-testid="portfolio-dialog-close"
                  className={cn(
                    "absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110",
                    isDark ? "bg-white/10 border border-white/20" : "bg-black/10 border border-black/20"
                  )}
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="p-8 md:p-10">
                <DialogHeader>
                  <div className="flex items-center justify-between mb-3">
                    <span className={cn("text-[10px] uppercase tracking-[0.3em]", isDark ? "text-white/55" : "text-black/55")}>
                      {active.category}
                    </span>
                    <span className={cn("text-[10px] uppercase tracking-[0.3em]", isDark ? "text-white/55" : "text-black/55")}>
                      {active.year}
                    </span>
                  </div>
                  <DialogTitle className="font-display font-semibold text-3xl md:text-4xl tracking-tighter">
                    {active.title}
                  </DialogTitle>
                  <DialogDescription className={cn("font-body", isDark ? "text-white/70" : "text-black/65")}>
                    Étude de cas
                  </DialogDescription>
                </DialogHeader>

                <div className="mt-8 space-y-6">
                  <Detail label="Brief" text={active.brief} isDark={isDark} />
                  <Detail label="Solution" text={active.solution} isDark={isDark} />
                  <Detail label="Résultat" text={active.outcome} isDark={isDark} />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Detail({ label, text, isDark }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <span
        className={cn(
          "text-[10px] uppercase tracking-[0.35em] pt-1",
          isDark ? "text-white/50" : "text-black/45"
        )}
      >
        {label}
      </span>
      <p
        className={cn(
          "md:col-span-3 text-base leading-relaxed font-body font-light",
          isDark ? "text-white/85" : "text-black/80"
        )}
      >
        {text}
      </p>
    </div>
  );
}

function ProjectCard({ project, isDark, onOpen }) {
  return (
    <button
      onClick={onOpen}
      data-testid={`portfolio-item-${project.id}`}
      className={cn(
        "group block text-left w-full overflow-hidden transition-all duration-500 ease-in-out",
        "hover:-translate-y-2"
      )}
    >
      <div
        className={cn(
          "relative aspect-[4/5] overflow-hidden rounded-2xl",
          isDark ? "bg-white/[0.03] border border-white/[0.08]" : "bg-black/[0.02] border border-black/[0.06]"
        )}
      >
        <img
          src={project.image}
          alt={project.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-in-out"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: isDark
              ? "linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.7) 100%)"
              : "linear-gradient(180deg, transparent 50%, rgba(255,255,255,0.85) 100%)",
          }}
        />
        <div
          className={cn(
            "absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500",
            isDark ? "bg-white text-black" : "bg-black text-white"
          )}
        >
          <ArrowUpRight size={16} />
        </div>
        <div className="absolute bottom-5 left-5 right-5">
          <div className={cn("text-[10px] uppercase tracking-[0.3em] mb-1", isDark ? "text-white/70" : "text-black/65")}>
            {project.category}
          </div>
          <h3 className={cn("font-display font-semibold text-xl md:text-2xl tracking-tight", isDark ? "text-white" : "text-black")}>
            {project.title}
          </h3>
        </div>
      </div>
    </button>
  );
}
